import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { Seo } from '@/layout/Seo'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl'
import { ContentOnlyLayout } from '@/layouts/ContentOnlyLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { getEmailCapturePageBySlug } from '@/services/EmailCapturePageService'
import { EmailCapturePageConfig, getWatchThumbnails } from '@/services/EmailCapturePageService/EmailCapturePageService'
import { Faq } from '@/services/FaqService'
import { getProjectMeta } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { logger } from '@/utils/logging'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { SignUpView } from '@/views/SignUpView'

interface SignUpProps {
  emailCaptureConfig: EmailCapturePageConfig
  logoCloudinaryPath: string | null
  faqs: Faq[]
}

const SignUp: NextPageWithLayout<SignUpProps> = ({ emailCaptureConfig, logoCloudinaryPath, faqs }) => {
  const { slug, openGraphTitle, openGraphDescription, openGraphImageCloudinaryPath, ctaButtonHref, ctaButtonText } =
    emailCaptureConfig
  const { t } = useTranslate('sign-up')
  const url = useCanonicalUrl(`${paths.signup.index}/${slug}`)
  const description =
    openGraphDescription ??
    'As an Angel Guild member, you can vote on upcoming shows, receive free tickets to every theatrical release, gain early access to shows, receive merchandise discounts, and continue to amplify light.'

  return (
    <>
      <Seo
        canonical={url}
        title={openGraphTitle}
        description={description}
        openGraph={{
          title: openGraphTitle,
          description: description,
          images: [
            {
              url: getCloudinaryImageUrl({
                path: openGraphImageCloudinaryPath,
                height: 630,
                width: 1200,
                format: 'webp',
                transforms: 'c_thumb',
              }),
              width: 1200,
              height: 630,
              alt: openGraphTitle,
            },
          ],
          url,
        }}
      />
      <SignUpView emailCaptureConfig={emailCaptureConfig} logoCloudinaryPath={logoCloudinaryPath} faqs={faqs} />
      <MarketingVideoPopup
        ctaHref={ctaButtonHref || '#email-capture'}
        ctaText={ctaButtonText || t('signupForUpdates', 'Sign Up for Updates')}
      />
    </>
  )
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<SignUpProps>(
  { filePath: 'pages/[locale]/signup/[slug].tsx' },
  async ({ federationClient, locale, next404, nextRedirect, params, preview }) => {
    const slug = getItemOrFirstEntry(params?.slug)

    if (!slug) return next404()
    if (/chosen/i.test(slug)) return nextRedirect(paths.signup.index)
    if (slug === slugs.truthAndTreasonSeries || slug === slugs.truthAndConviction)
      return nextRedirect(paths.signup.truthAndTreason)

    const [{ config, faqs }, translations] = await Promise.all([
      getEmailCapturePageBySlug({ opts: { locale, preview }, slug: slug.toLowerCase() }),
      loadTranslations(locale, ['common', 'sign-up', 'app-promo']),
    ])

    if (!config) return next404(Durations.FIVE_MINUTES_IN_SECONDS)

    if (!config.watchImages && config.projectSlug) {
      config.watchImages = await getWatchThumbnails({ slug: config.projectSlug, locale })
    }

    let logoCloudinaryPath = config.logoCloudinaryPath

    try {
      if (config.projectSlug && config.projectSlug !== 'angel-studios') {
        const result = await getProjectMeta({ slug: config.projectSlug }, federationClient, {
          locale,
          preview,
        })
        logoCloudinaryPath = result.logoCloudinaryPath ?? config.logoCloudinaryPath
      }
    } catch (e) {
      logger().error(`Error looking up project logo from hydra projectSlug`, { slug: config.projectSlug, locale }, e)
    }

    return {
      props: {
        faqs,
        emailCaptureConfig: config,
        logoCloudinaryPath: logoCloudinaryPath ?? null,
        ...translations,
      },
      revalidate: Durations.FIVE_MINUTES_IN_SECONDS,
    }
  },
)

SignUp.getLayout = (page) => {
  return <ContentOnlyLayout>{page}</ContentOnlyLayout>
}

export default SignUp
